/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import { onClickNavUrl } from '@coursera-web/cds-labs';
import type { Maybe } from 'graphql/jsutils/Maybe';

import useRouter from 'js/lib/useRouter';
import type UserAgentInfo from 'js/lib/useragent';

import { Button, Grid, Typography, Typography2, breakpoints, useTheme } from '@coursera/cds-core';
import type { Theme } from '@coursera/cds-core';
import { ArrowNextIcon } from '@coursera/cds-icons';

import type {
  EntWebsiteBenefitFragment,
  EntWebsiteBenefitListFragment,
  EntWebsiteBlockBenefitListQuery,
} from 'bundles/ent-website/__generated__/contentfulTypes';
// FIXME: existing import/no-cycle violations are excused to prevent seeing errors when modifying other parts of the same file; please fix it carefully
// eslint-disable-next-line import/no-cycle
import BlockLayout, { noImage } from 'bundles/ent-website/contentful/core/BlockLayout';
import { entWebsiteBlockBenefitListQuery } from 'bundles/ent-website/queries/EntWebsiteBenefitList';
import type { BlockModuleInnerProps } from 'bundles/ent-website/utils/withBlockQuery';
import withBlockQuery from 'bundles/ent-website/utils/withBlockQuery';

type Props = BlockModuleInnerProps<EntWebsiteBenefitListFragment>;

type PropsFromCaller = {
  userAgent?: UserAgentInfo;
};

type PropsWithDesktopGrid = {
  benefit: EntWebsiteBenefitFragment;
  gridItemCount: number;
};

const styles = {
  container: () => css`
    ${breakpoints.down('xs')} {
      width: auto;
      margin: calc(var(--cds-spacing-600) * -1) calc(var(--cds-spacing-200) * -1);
    }
  `,
  headline: (_: Theme, color?: Maybe<string>) => css`
    padding-bottom: var(--cds-spacing-200);
    font-size: 32px;
    font-weight: 600;
    line-height: 1;
    color: ${color ?? 'var(--cds-color-grey-975)'};
  `,
  subhead: (_: Theme, color?: Maybe<string>) => css`
    padding-bottom: var(--cds-spacing-400);
    font-size: 16px;
    line-height: 1.5;
    flex: 1;
    color: ${color ?? 'var(--cds-color-grey-975)'};

    ${breakpoints.down('sm')} {
      font-size: 14px;
    }
  `,
  link: (_: Theme, color?: Maybe<string>) => css`
    width: fit-content;
    font-size: 16px;
    line-height: 1.5;
    color: ${color ?? 'var(--cds-color-blue-700)'} !important;
    background-color: transparent !important;
    padding-left: 0;

    ${breakpoints.down('sm')} {
      font-size: 14px;
    }
  `,
  gridBackgroundStyling: () => css`
    height: 100%;
    background-position: bottom right;
    background-size: cover;
    border-radius: 8px;
    display: flex;
    flex-direction: column;
  `,
  singleGridItemBackgroundStyling: (theme: Theme) => css`
    height: 100%;
    background-position: center;
    background-size: cover;
    border-radius: 8px;
    padding-top: 60px !important;

    ${breakpoints.down('sm')} {
      padding-top: 40px !important;
      background-position: right 25% bottom -15%;

      a {
        font-size: ${theme.typography.body2.fontSize};
      }
    }

    h2,
    p {
      max-width: 520px;
    }

    h2 {
      padding-bottom: var(--cds-spacing-400);

      ${breakpoints.down('sm')} {
        padding-bottom: var(--cds-spacing-300);
      }
    }
  `,
  twoItemPadding: () => css`
    padding: var(--cds-spacing-800) var(--cds-spacing-600) var(--cds-spacing-800);

    ${breakpoints.down('sm')} {
      padding: 40px var(--cds-spacing-200) 40px;
    }
  `,
  threeItemPadding: () => css`
    padding: var(--cds-spacing-600) var(--cds-spacing-400) var(--cds-spacing-800);

    ${breakpoints.down('sm')} {
      padding: 40px var(--cds-spacing-200) 40px;
    }
  `,
  card: css`
    cursor: pointer;
    text-decoration: none !important;

    :hover {
      a,
      button {
        text-decoration: underline;
      }
    }
  `,
};

export function BenefitGrid({ benefit, gridItemCount }: PropsWithDesktopGrid) {
  const theme = useTheme();
  const { headline, headlineColor, subhead, subheadColor, ctaText, ctaTarget, ctaColor, image } = benefit;
  const imageUrl: string = image && image.url ? image.url : '#';
  const router = useRouter();

  if (!ctaText || !ctaTarget) return null;

  return (
    <Grid component="a" href={ctaTarget ?? ''} item xs={12} sm css={styles.card}>
      <div
        css={[
          {
            background: `url(${imageUrl})`,
          },
          gridItemCount === 1 ? styles.singleGridItemBackgroundStyling : styles.gridBackgroundStyling,
          gridItemCount > 1 && gridItemCount === 2 ? styles.twoItemPadding : styles.threeItemPadding,
        ]}
      >
        <Typography variant="h2semibold" css={styles.headline(theme, headlineColor)}>
          {headline}
        </Typography>
        {subhead && (
          <Typography2 component="p" variant="bodySecondary" css={styles.subhead(theme, subheadColor)}>
            {subhead}
          </Typography2>
        )}
        <Button
          onClick={onClickNavUrl(router, ctaTarget)}
          variant="ghost"
          icon={<ArrowNextIcon size="small" />}
          css={styles.link(theme, ctaColor)}
        >
          {ctaText}
        </Button>
      </div>
    </Grid>
  );
}

export function BenefitListModule({ content, assembly = {} }: Props & PropsFromCaller) {
  const { visibility } = assembly;

  if (!content) {
    return null;
  }

  const { benefitsCollection } = content;

  if (!visibility?.benefits || !benefitsCollection || !benefitsCollection?.items) {
    return null;
  }

  const gridItemCount = benefitsCollection.items.length;

  const contentPanel = () => (
    <Grid container spacing={16} direction="row" css={styles.container}>
      {benefitsCollection?.items.map((benefit) => {
        if (benefit) {
          return <BenefitGrid key={benefit.slug} {...{ benefit, gridItemCount }} />;
        } else return null;
      })}
    </Grid>
  );

  return <BlockLayout assembly={noImage(assembly)} renderContent={contentPanel} />;
}

// @ts-expect-error TSMIGRATION-5.0 TS2344
export const BlockBenefitListModule = withBlockQuery<EntWebsiteBenefitListFragment, EntWebsiteBlockBenefitListQuery>(
  entWebsiteBlockBenefitListQuery
)(BenefitListModule);
